import ScrollArea from "../ui/shared/ScrollArea";
import { ChatHeader } from "./ChatHeader";
import { MessageInput } from "./MessageInput";
import AudioMessage from "./AudioMessage";
import MediaMessage from "./MediaMessage";
import { supabase, type Database } from "@/lib/supabase";
import { getDisplayName } from "@/utils/contactUtils";
import { formatMessageTime } from "@/utils/messageUtils";
import { formatWhatsAppText } from "@/lib/whatsappTextFormat";
import { messageBelongsToContact } from "@/lib/whatsappJid";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Brain, Repeat } from "lucide-react";
import type { Fluxo } from "@/hooks/useFluxos";

type AccountMessage = Database['public']['Tables']['account_messages']['Row']
type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface OptimisticMessage extends Omit<AccountMessage, 'id'> {
  id: string | number
  isOptimistic?: boolean
  isSending?: boolean
  sendError?: string
  account_contact_id?: string | null
}
type AssistantNamesMap = Record<string, string>;

interface MessageBubbleProps {
  message: OptimisticMessage;
  sender: "user" | "bot";
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
}

interface MessageItemProps {
  message: OptimisticMessage;
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
}

interface MessagesAreaProps {
  messages: OptimisticMessage[];
  loading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
}

interface ChatAreaProps {
  selectedContact: AccountContact | null;
  messages: OptimisticMessage[];
  onSendImage: (imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendVideo: (videoBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendDocument: (documentBase64: string, fileName: string, caption?: string) => Promise<boolean>;
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: () => void;
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  onSendFluxo?: (fluxo: Fluxo) => void;
  loading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  sending?: boolean;
  instance?: string;
  apiKey?: string;
  onContactUpdate?: () => void;
  onContactUpdateOptimistic?: (updates: Partial<AccountContact>) => void;
}

const MessageBubble = ({ message, sender, instance, apiKey, assistantNamesMap }: MessageBubbleProps) => {
  const [localTranscription, setLocalTranscription] = useState<string | null>(
    (message as any).transcription || null
  );
  const [localProcessedUrl, setLocalProcessedUrl] = useState<string | null>(
    (message as any).processed_media_url || (message as any).media_url || null
  );

  const messageTime = formatMessageTime(message.sent_at);
  const isAudio = message.media_kind === 'audioMessage' || message.media_kind === 'audio';
  const isImage = message.media_kind === 'imageMessage' || message.media_kind === 'image';
  const isVideo = message.media_kind === 'videoMessage' || message.media_kind === 'video';
  const isDocument = message.media_kind === 'documentMessage' || message.media_kind === 'document';
  const hasMedia = isAudio || isImage || isVideo || isDocument;
  const isAiMessage = message.from_me && message.is_from_ai;
  const isFollowupMessage = message.from_me && (message as any).is_followup === true;

  const messageId = typeof message.id === 'string' ? message.id : String(message.id);

  // Check if media_url is from WhatsApp (mmg.whatsapp.net)
  const mediaUrl = (message as any).media_url;
  const isWhatsAppMedia = mediaUrl && typeof mediaUrl === 'string' && mediaUrl.includes('mmg.whatsapp.net');

  // Check if this is a document sent from dashboard (stored in our Supabase Storage)
  const rawPayload = (message as any).raw_payload;
  const isSentDocument = message.from_me &&
                         rawPayload?.sent_via_dashboard === true &&
                         rawPayload?.storage_bucket === 'sent-documents';

  // Check if media_url is from Supabase Storage
  const isSupabaseStorage = mediaUrl && typeof mediaUrl === 'string' &&
                           (mediaUrl.includes('supabase.co/storage') || mediaUrl.includes('/storage/v1/object/public/'));

  // Determine effective media URL with priority:
  // 1. processed_media_url if available (already processed)
  // 2. media_url if it's a document sent from dashboard (already in our storage)
  // 3. media_url if it's from Supabase Storage (images/videos/audio sent by us)
  // 4. media_url if it's from WhatsApp (needs processing)
  // 5. null (no media available)
  const effectiveMediaUrl = (message as any).processed_media_url ||
                           (isSentDocument ? mediaUrl : null) ||
                           (isSupabaseStorage ? mediaUrl : null) ||
                           (isWhatsAppMedia ? mediaUrl : null);

  const assistantId = (message as any).account_assistant_id;
  const assistantName = assistantId && assistantNamesMap ? assistantNamesMap[assistantId] : null;

  const getBubbleBackground = () => {
    if (sender === "bot") {
      return "bg-white border border-border shadow-sm dark:bg-neutral-600 dark:border-transparent dark:shadow-none";
    }
    if (isAiMessage) {
      return "bg-blue-50 border border-blue-100 dark:bg-[#1a365d] dark:border-transparent";
    }
    if (isFollowupMessage) {
      return "bg-violet-50 border border-violet-100 dark:bg-[#3b2f5e] dark:border-transparent";
    }
    return "bg-[#DCF8C6] border border-[#c5e8b0] shadow-sm dark:bg-[#005C4B] dark:border-transparent dark:shadow-none";
  };

  return (
    <div
      className={`flex flex-col ${
        sender === "user" ? "items-end" : "items-start"
      }`}
    >
      <div
        className={`${hasMedia ? 'max-w-md' : 'max-w-[220px]'} px-3 py-2 rounded-2xl text-foreground dark:text-neutral-50 relative ${
          getBubbleBackground()
        } ${
          sender === "user" ? "rounded-br-sm" : "rounded-bl-sm"
        } ${message.isOptimistic && message.isSending ? 'opacity-70' : ''} ${message.sendError ? 'border border-red-500/50' : ''}`}
      >
        {message.isOptimistic && message.isSending && (
          <div className="absolute -top-1 -right-1 w-3 h-3">
            <div className="w-3 h-3 border-2 border-neutral-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        {message.sendError && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">!</span>
          </div>
        )}

        {message.text_content && !hasMedia && <p className="text-[13px] whitespace-pre-wrap break-words">{formatWhatsAppText(message.text_content)}</p>}

        {isAudio && message.wa_message_id && (
          <AudioMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            existingProcessedUrl={effectiveMediaUrl}
            transcription={localTranscription}
            onUrlProcessed={(url) => setLocalProcessedUrl(url)}
            onTranscriptionProcessed={(transcription) => setLocalTranscription(transcription)}
            isSending={message.isSending}
          />
        )}

        {isImage && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="image"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isVideo && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="video"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isDocument && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="document"
            existingProcessedUrl={effectiveMediaUrl}
            isSending={message.isSending}
            fileName={(message.raw_payload as any)?.fileName}
          />
        )}

        {message.text_content && hasMedia && (
          <p className="text-[13px] mt-2 whitespace-pre-wrap break-words">{formatWhatsAppText(message.text_content)}</p>
        )}

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50 dark:border-white/10">
          <div className={`text-xs ${message.sendError ? 'text-red-500 dark:text-red-300' : 'text-muted-foreground dark:text-neutral-300'}`}>
            {message.sendError ? 'Erro ao enviar' : messageTime}
          </div>
          {isAiMessage && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-[#1a365d]/50 dark:text-white/90">
              <Brain className="w-2.5 h-2.5" />
              <span className="text-[10px] font-medium">
                Assistente - {assistantName || 'IA'}
              </span>
            </div>
          )}
          {isFollowupMessage && !isAiMessage && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-[#3b2f5e]/60 dark:text-white/90">
              <Repeat className="w-2.5 h-2.5" />
              <span className="text-[10px] font-medium">
                Follow-up
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MessageItem = ({ message, instance, apiKey, assistantNamesMap }: MessageItemProps) => {
  const sender = message.from_me ? "user" : "bot";

  return (
    <div
      className={`flex items-start space-x-3 ${
        sender === "user" ? "flex-row-reverse space-x-reverse" : ""
      }`}
    >
      <MessageBubble
        message={message}
        sender={sender}
        instance={instance}
        apiKey={apiKey}
        assistantNamesMap={assistantNamesMap}
      />
    </div>
  );
};

const MessagesArea = ({ messages, loading, hasMore, isLoadingMore, onLoadMore, instance, apiKey, assistantNamesMap }: MessagesAreaProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const [isNearBottom, setIsNearBottom] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const previousMessagesLengthRef = useRef(messages.length)
  const previousScrollHeightRef = useRef(0)
  const isLoadingMoreRef = useRef(false)

  // Verifica se o usuário está próximo do final
  const checkIfNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    const threshold = 100 // pixels do final
    const isNear = scrollHeight - scrollTop - clientHeight < threshold
    setIsNearBottom(isNear)
  }, [])

  // Scroll para o final apenas se estiver próximo
  const scrollToBottom = useCallback((force = false) => {
    if (messagesEndRef.current && (isNearBottom || force)) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [isNearBottom])

  // Handler de scroll com debounce
  const handleScroll = useCallback(() => {
    checkIfNearBottom()
  }, [checkIfNearBottom])

  useEffect(() => {
    if (messages.length === 0) return

    const previousLength = previousMessagesLengthRef.current
    const hasNewMessage = messages.length > previousLength

    if (hasNewMessage && isNearBottom) {
      scrollToBottom()
    }

    previousMessagesLengthRef.current = messages.length
  }, [messages, isNearBottom, scrollToBottom])

  // Scroll imediato ao trocar de contato
  useEffect(() => {
    if (messages.length > 0) {
      setIsNearBottom(true)
      previousMessagesLengthRef.current = messages.length
      setTimeout(() => {
        scrollToBottom(true) // força scroll ao trocar de contato
      }, 100)
    }
  }, [messages.length > 0 ? messages[0]?.remote_jid : null, scrollToBottom])

  // Configurar IntersectionObserver para detectar scroll no topo
  useEffect(() => {
    if (!topSentinelRef.current || !hasMore || loading) return

    const currentSentinel = topSentinelRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && !isLoadingMoreRef.current && hasMore) {
          console.log('🔝 [MessagesArea] Top sentinel visible, loading more messages')
          isLoadingMoreRef.current = true
          onLoadMore()
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '50px',
        threshold: 0.1
      }
    )

    if (currentSentinel) {
      observer.observe(currentSentinel)
    }

    return () => {
      try {
        if (currentSentinel && observer) {
          observer.unobserve(currentSentinel)
        }
        observer.disconnect()
      } catch (error) {
        console.warn('⚠️ [MessagesArea] Error disconnecting observer:', error)
      }
    }
  }, [hasMore, loading, onLoadMore])

  // Reset loading flag quando isLoadingMore mudar
  useEffect(() => {
    if (!isLoadingMore) {
      isLoadingMoreRef.current = false
    }
  }, [isLoadingMore])

  // Manter posição do scroll ao carregar mensagens antigas
  useEffect(() => {
    if (!scrollContainerRef.current || !isLoadingMore) return

    const scrollContainer = scrollContainerRef.current
    previousScrollHeightRef.current = scrollContainer.scrollHeight
  }, [isLoadingMore])

  useEffect(() => {
    if (!scrollContainerRef.current || isLoadingMore) return

    const scrollContainer = scrollContainerRef.current
    const previousScrollHeight = previousScrollHeightRef.current
    const currentScrollHeight = scrollContainer.scrollHeight

    if (previousScrollHeight > 0 && currentScrollHeight > previousScrollHeight) {
      const heightDifference = currentScrollHeight - previousScrollHeight
      scrollContainer.scrollTop = scrollContainer.scrollTop + heightDifference
      console.log('📏 [MessagesArea] Adjusted scroll after loading more:', heightDifference)
    }
  }, [messages.length, isLoadingMore])

  // Atualizar botão de scroll e contador de não lidas
  useEffect(() => {
    const shouldShow = !isNearBottom && messages.length > 0
    setShowScrollButton(shouldShow)

    if (!isNearBottom) {
      const lastMessages = messages.slice(-5)
      const unreadMessages = lastMessages.filter(msg => !msg.from_me)
      setUnreadCount(unreadMessages.length)
    } else {
      setUnreadCount(0)
    }
  }, [isNearBottom, messages])

  // Handler para clicar no botão de scroll
  const handleScrollToBottom = useCallback(() => {
    setIsNearBottom(true)
    scrollToBottom(true)
    setUnreadCount(0)
  }, [scrollToBottom])

  // Verificar posição inicial
  useEffect(() => {
    checkIfNearBottom()
  }, [checkIfNearBottom])

  if (loading) {
    return (
      <ScrollArea classNames={{ root: "flex-1 p-3 h-full" }}>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-card-muted dark:bg-neutral-600 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded mb-2"></div>
                <div className="h-3 bg-card-muted dark:bg-neutral-600 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  }

  if (messages.length === 0) {
    return (
      <ScrollArea classNames={{ root: "flex-1 h-full" }}>
        <div className="p-4 flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground dark:text-neutral-50 mb-2">Nenhuma mensagem</h3>
            <p className="text-muted-foreground dark:text-neutral-300 text-sm">
              Inicie uma conversa enviando uma mensagem.
            </p>
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <div className="relative flex-1 h-full">
      <ScrollArea
        classNames={{
          root: "flex-1 h-full"
        }}
      >
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="p-3 space-y-2"
          style={{ scrollBehavior: 'auto' }}
        >
          {/* Sentinela no topo para paginação */}
          {hasMore && messages.length > 0 && (
            <div ref={topSentinelRef} className="h-1" />
          )}

          {/* Indicador de carregamento no topo */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-3">
              <div className="flex items-center gap-2 text-muted-foreground dark:text-neutral-400 text-sm">
                <div className="w-4 h-4 border-2 border-muted-foreground dark:border-neutral-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Carregando mensagens antigas...</span>
              </div>
            </div>
          )}

          {/* Indicador de início da conversa */}
          {!hasMore && !loading && messages.length > 0 && (
            <div className="flex items-center justify-center py-3">
              <div className="text-muted-foreground dark:text-neutral-500 text-xs">
                Início da conversa
              </div>
            </div>
          )}

          {/* Mensagens */}
          {messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              instance={instance}
              apiKey={apiKey}
              assistantNamesMap={assistantNamesMap}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Botão flutuante para novas mensagens */}
      {showScrollButton && (
        <button
          onClick={handleScrollToBottom}
          className="absolute bottom-4 right-4 bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:text-white rounded-full p-3 shadow-lg transition-all duration-200 flex items-center gap-2 z-10"
          aria-label="Ir para novas mensagens"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          {unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export const ChatArea = ({
  selectedContact,
  messages,
  message,
  setMessage,
  onSendMessage,
  onSendAudio,
  onSendImage,
  onSendVideo,
  onSendDocument,
  onSendFluxo,
  loading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  sending = false,
  instance,
  apiKey,
  onContactUpdate,
  onContactUpdateOptimistic
}: ChatAreaProps) => {
  const [assistantNamesMap, setAssistantNamesMap] = useState<AssistantNamesMap>({})

  const aiMessageAssistantIds = useMemo(() => {
    const ids = new Set<string>()
    messages.forEach(msg => {
      const assistantId = (msg as any).account_assistant_id
      if (msg.is_from_ai && assistantId) {
        ids.add(assistantId)
      }
    })
    return Array.from(ids)
  }, [messages])

  useEffect(() => {
    const fetchAssistantNames = async () => {
      if (!supabase || aiMessageAssistantIds.length === 0) return

      const missingIds = aiMessageAssistantIds.filter(id => !assistantNamesMap[id])
      if (missingIds.length === 0) return

      try {
        const { data, error } = await supabase
          .from('account_assistants')
          .select('id, name')
          .in('id', missingIds)

        if (error) throw error

        if (data && data.length > 0) {
          setAssistantNamesMap(prev => {
            const newMap = { ...prev }
            data.forEach(assistant => {
              newMap[assistant.id] = assistant.name
            })
            return newMap
          })
        }
      } catch (error) {
        console.error('Error fetching assistant names:', error)
      }
    }

    fetchAssistantNames()
  }, [aiMessageAssistantIds])

  // Filtrar mensagens do contato selecionado (JID normalizado + account_contact_id)
  const filteredMessages = useMemo(() => {
    if (!selectedContact || messages.length === 0) return messages

    return messages.filter((msg) => {
      if (messageBelongsToContact(msg, selectedContact)) return true

      console.warn('[ChatArea] Filtering message that does not belong to contact:', {
        messageJid: msg.remote_jid,
        expectedJid: selectedContact.jid,
        messageContactId: msg.account_contact_id,
        expectedContactId: selectedContact.id,
        messageId: msg.id
      })
      return false
    })
  }, [selectedContact, messages])

  if (!selectedContact) {
    return (
      <div className="w-full h-full flex flex-col border rounded-xl bg-card border-border shadow-sm dark:bg-[#20202040] dark:border-[#3F3F464D] dark:shadow-none max-w-[720px] mx-auto">
        <div className="flex-1 flex items-center justify-center chat-wallpaper rounded-xl">
          <div className="text-center">
            <div className="w-16 h-16 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground dark:text-neutral-50 mb-2">Selecione uma conversa</h3>
            <p className="text-muted-foreground dark:text-neutral-300 text-sm">
              Escolha um contato na lista para iniciar ou continuar a conversa.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col border rounded-xl bg-card border-border shadow-sm dark:bg-[#20202040] dark:border-[#3F3F464D] dark:shadow-none max-w-[720px] mx-auto overflow-hidden">
      <ChatHeader 
        chatName={getDisplayName(selectedContact)} 
        isGroup={selectedContact.is_group}
        selectedContact={selectedContact}
        onContactUpdate={onContactUpdate}
      />
      <div className="flex-1 overflow-hidden w-full min-w-[280px] chat-wallpaper">
        <MessagesArea
          messages={filteredMessages}
          loading={loading}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          instance={instance}
          apiKey={apiKey}
          assistantNamesMap={assistantNamesMap}
        />
      </div>
      <div className="border-t border-border bg-card dark:border-transparent dark:bg-transparent p-3">
        <MessageInput
          message={message}
          setMessage={setMessage}
          onSendMessage={onSendMessage}
          onSendAudio={onSendAudio}
          onSendImage={onSendImage}
          onSendVideo={onSendVideo}
          onSendDocument={onSendDocument}
          onSendFluxo={onSendFluxo}
          sending={sending}
          selectedContact={selectedContact}
        />
      </div>
    </div>
  );
};
